import { useEffect, useState, useRef } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { useTranslations } from "use-intl"
import { useSelector } from "react-redux"
import {
  Counter,
  FetchError,
  JobDetailCard,
  JobPreviewCards,
  useFindJobs,
  ZeroState,
  SaveSubscriptionCard,
} from "."
import { JobPreviewCardSkeleton } from "components/Skeletons"
import { ModalWrapper } from "components/ModalWrapper"
import { Select } from "components/Form"
import { appendLocale, cleanJobPreviewCards, fetchJobs } from "utils"
import { useDebounce } from "hooks"
import { FaBookmark, FaSpinner } from "react-icons/fa"
import getConfig from "next/config"
import { sanitize } from "utils/sanitize"
import { jobSearchSchema } from "utils/validationSchema"

export function JobSearch({
  which,
  query: initialQuery,
  locale,
  atlasRef,
  isTester,
}) {
  const { publicRuntimeConfig } = getConfig()

  const t = useTranslations("Shared")
  const j = useTranslations("JobSearch")
  const r = useTranslations("JobSearch.Results")
  const [count, setCount] = useState()
  const [facetsFromAzure, setFacetsFromAzure] = useState()
  const [data, setData] = useState()
  const showLoadMore = data?.length < count
  const [error, setError] = useState()
  const [page, setPage] = useState(0)
  const [query, setQuery] = useState(initialQuery)
  const settledQuery = useDebounce(query, 400)
  const [loadingMore, setLoadingMore] = useState(false)
  const [currentJob, setCurrentJob] = useState()
  const [subAlertStatus, setSubAlertStatus] = useState(false)
  const [azureFields, setAzureFields] = useState({})
  const [searchIsValid, setSearchIsValid] = useState(false)
  const [isThrottled, setIsThrottled] = useState(false)
  const [scrollActivity, setScrollActivity] = useState(0)
  const [throttleDelay, setThrottleDelay] = useState(1000) // Start with 1s
  const MAX_THROTTLE = 15000 // Max 15 second delay
  const MIN_THROTTLE = 1000 // Min 1 second delay
  const COOLDOWN_TIME = 5000 // 5 seconds of no scrolling to start easing up
  const SCROLL_THRESHOLD = 1 // Number of scrolls before increasing throttle
  const THROTTLE_INCREASE_RATE = 1.75 // Increase delay by 75% each time
  const THROTTLE_DECREASE_RATE = 0.5 // Decrease delay by 75% during cooldown
  const [debouncedPage, setDebouncedPage] = useState(0)
  const debouncedPageValue = useDebounce(debouncedPage, 0)
  const [scrollCount, setScrollCount] = useState(0) // Add this new state
  const INITIAL_FREE_SCROLLS = 3 // Number of scrolls before throttling begins

  const { FindJobs, register, watch, isValid } = useFindJobs({
    which,
    tw: "pb-2",
    showHeading: false,
    initialQuery,
    facetsFromAzure,
  })

  const current = watch()

  const { search, location } = useSelector((state) => state[which])
  const { radius, wage, start_date, job_type, job_status, facets, sort } =
    current
  const cleanCards = (listing, l) => {
    return cleanJobPreviewCards({
      listing,
      locale: l,
      t,
    })
  }

  useEffect(() => {
    const validateSearch = async () => {
      try {
        await jobSearchSchema()?.validate({ search, location })
        setSearchIsValid(true)
      } catch (e) {
        setSearchIsValid(false)
      }
    }
    validateSearch()
  }, [search, location])

  useEffect(() => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      search,
      location,
      radius,
      wage,
      start_date,
      job_type,
      job_status,
      facets,
      sort,
    }))
  }, [
    search,
    location,
    radius,
    wage,
    start_date,
    job_type,
    job_status,
    facets,
    sort,
  ])

  const fetchInitialData = async () => {
    try {
      if (!isValid) {
        return setData([])
      }
      const { data, azFilter, azOrderBy, azSearch } = await fetchJobs(
        which,
        sanitize(settledQuery),
        0,
        locale
      )
      setAzureFields({ azFilter, azOrderBy, azSearch })
      const cleaned = data.value.map((listing) => cleanCards(listing, locale))
      setPage(0)
      setData(cleaned)
      if (cleaned && cleaned[0]) {
        setCurrentJob(cleaned[0].case_number)
      }
      setCount(data["@odata.count"])
      if (facets.length === 0) {
        setFacetsFromAzure(
          data["@search.facets"][`job_title${appendLocale(locale)}`]
        )
      }
      setLoadingMore(false)
      setError(null)
    } catch (e) {
      setError(e)
      setLoadingMore(false)
    }
  }

  const fetchMoreData = async (pageNum) => {
    if (pageNum === 0) return // Skip if it's initial page

    try {
      const { data, azFilter, azOrderBy, azSearch } = await fetchJobs(
        which,
        sanitize(query),
        pageNum,
        locale
      )
      setAzureFields({ azFilter, azOrderBy, azSearch })
      const cleaned = data?.value.map((listing) => cleanCards(listing, locale))
      setData((prevData) => [...prevData, ...cleaned])
      setError(null)
    } catch (e) {
      setError(e)
    } finally {
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    fetchInitialData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [which, settledQuery, locale, isValid])

  useEffect(() => {
    if (debouncedPageValue === 0) return
    fetchMoreData(debouncedPageValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPageValue]) // Only depend on debounced page changes

  useEffect(() => {
    let cooldownTimer

    if (scrollActivity > SCROLL_THRESHOLD) {
      setThrottleDelay((prev) =>
        Math.min(MAX_THROTTLE, prev * THROTTLE_INCREASE_RATE)
      )
      setScrollActivity(0)
    }

    // Start cooldown timer
    cooldownTimer = setTimeout(() => {
      setThrottleDelay((prev) =>
        Math.max(MIN_THROTTLE, prev * THROTTLE_DECREASE_RATE)
      )
      setScrollActivity(0)
    }, COOLDOWN_TIME)

    return () => clearTimeout(cooldownTimer)
  }, [scrollActivity])

  const next = () => {
    if (loadingMore) return

    // Track total scroll count
    setScrollCount((prev) => prev + 1)

    // Only start throttling after INITIAL_FREE_SCROLLS
    if (scrollCount >= INITIAL_FREE_SCROLLS) {
      const newScrollActivity = scrollActivity + 1
      const shouldThrottle = newScrollActivity >= SCROLL_THRESHOLD

      setScrollActivity(newScrollActivity)

      if (shouldThrottle) {
        setIsThrottled(true)
        setTimeout(() => {
          setIsThrottled(false)
        }, throttleDelay)
      }
    }

    // Batch state updates
    setLoadingMore(true)
    setDebouncedPage((prevPage) => prevPage + 1)
    setPage((prevPage) => prevPage + 1)
  }

  // modal props (Filterbox)
  const [open, setOpen] = useState(false)

  const onOpenModal = () => setOpen(true)
  const onCloseModal = () => setOpen(false)
  // end modal props

  const [subscriptionStatus, setSubscription] = useState(false)
  const onOpenSubscription = () => setSubscription(true)
  const onCloseSubscription = () => setSubscription(false)

  const renderSelect = () => {
    if (data && data.length) {
      return (
        <Select
          overrideTW="flex flex-row justify-between items-center space-x-1 lg:space-x-3 mr-2.5"
          selectTW="flex-1 flex-shrink h-9 mt-0 w-full py-0 md:w-40"
          label={r("sort_by")}
          labelTW="mt-0 text-xs"
          name="sort"
          options={[
            "relevancy",
            "begin_date",
            "basic_rate_from",
            "accepted_date",
          ]}
          optionMapper="sort_by_"
          optionT={r}
          register={register}
        />
      )
    }
  }

  const renderLoadMore = () => {
    if (showLoadMore) {
      return (
        <button
          type="button"
          disabled={
            loadingMore ||
            isThrottled ||
            (scrollCount >= INITIAL_FREE_SCROLLS && debouncedPage !== page)
          }
          className="usa-button mt-3 mr-0"
          onClick={() => {
            next()
          }}
        >
          {loadingMore || isThrottled ? (
            <span className="flex items-center justify-center">
              <FaSpinner className="animate-spin mr-2" />
              {j("loading")}
            </span>
          ) : (
            j("load_more")
          )}
        </button>
      )
    }
  }

  const saveSearch = () => {
    const is_not_inactive = job_status !== "inactive"
    const showSubButton =
      publicRuntimeConfig.subscription_toggle === "true" && is_not_inactive

    return (
      <>
        {showSubButton && searchIsValid ? (
          <>
            <div
              tabIndex={0}
              className="w-full bg-dolBlue text-white p-3 hover:underline cursor-pointer md:px-4 lg:px-8 lg:space-x-4 grid-container"
              onKeyUp={(e) => (e.key === "Enter" ? onOpenSubscription() : null)}
            >
              <span
                onClick={() => {
                  onOpenSubscription()
                }}
              >
                <FaBookmark style={{ color: "#ecc94b" }} className="inline" />
                {j("SaveSubscription")}
              </span>
            </div>
            <ModalWrapper
              open={subscriptionStatus}
              onCloseModal={onCloseSubscription}
              actualModal={true}
              alertModal={subAlertStatus}
            >
              <SaveSubscriptionCard
                search={sanitize(search)}
                location={sanitize(location)}
                radius={radius}
                wageType={wage}
                date={start_date}
                jobType={job_type}
                jobStatus={job_status}
                sort={sort}
                locale={locale}
                azureSearch={azureFields}
                close={onCloseSubscription}
                setAlert={setSubAlertStatus}
                isTester={isTester}
              />
            </ModalWrapper>
          </>
        ) : null}
      </>
    )
  }

  useEffect(() => {
    // Get the actual scrollable div from InfiniteScroll
    const scrollableDiv = document.querySelector(".infinite-scroll-component")
    if (scrollableDiv) {
      scrollableDiv.scrollTop = 0
      setPage(0)
      setDebouncedPage(0)
      setScrollCount(0)
      setIsThrottled(false)
      setLoadingMore(false)
    }
  }, [search, location])

  return (
    <>
      <div>
        <main id="main-content">
          {FindJobs}
          {error ? <FetchError error={error} /> : null}
          {data && data.length === 0 ? (
            <>
              {saveSearch()}
              <ZeroState />
            </>
          ) : (
            <>
              {search?.length > 0 || location?.length > 0 ? saveSearch() : null}
              <div className="mt-2 p-0 flex flex-1 flex-row space-x-2 grid-container">
                <div
                  className="flex flex-1 flex-col md:max-w-md"
                  style={{ maxHeight: "38rem" }}
                >
                  <div className="flex flex-col space-y-3 md:space-y-0 md:flex-row justify-between items-center mb-6">
                    <Counter
                      current={(data && data.length) || 0}
                      total={count}
                    />
                    {renderSelect()}
                  </div>

                  <InfiniteScroll
                    className="overflow-y w-screen md:w-full flex flex-1 flex-col space-y-2 md:-mt-2 md:-ml-2 md:pt-2 md:pl-2 md:pr-1"
                    dataLength={data?.length || 0}
                    next={next}
                    height="31.77rem"
                    hasMore={
                      !loadingMore && !isThrottled && data?.length < count
                    }
                    loader={<JobPreviewCardSkeleton />}
                    endMessage={
                      <p style={{ textAlign: "center" }}>
                        <b>
                          {!isThrottled &&
                            data?.length >= count &&
                            r("no_more")}
                        </b>
                      </p>
                    }
                    scrollThreshold={0.8}
                  >
                    <JobPreviewCards
                      which={which}
                      data={data}
                      current={currentJob}
                      setCurrentJob={setCurrentJob}
                      onOpenModal={onOpenModal}
                    />
                  </InfiniteScroll>
                  {renderLoadMore()}
                </div>
                <ModalWrapper open={open} onCloseModal={onCloseModal}>
                  <JobDetailCard case_number={currentJob} atlasRef={atlasRef} />
                </ModalWrapper>
              </div>
            </>
          )}
        </main>
      </div>
    </>
  )
}
